import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select, Pill } from "./ui";
import { toast } from "sonner";
import { Plus, Repeat, Ban } from "lucide-react";
import { naira } from "../../lib/format";

const thisPeriod = () => new Date().toISOString().slice(0, 7);

export default function RecurringDeductions() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/payroll/adjustments", { params: { recurring: true } })
    .then(setRows).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    api.get("/hr/employees").then(setEmployees).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.employee_id || !form.name || !Number(form.amount)) { toast.error("Employee, name and amount are required"); return; }
    setBusy(true);
    try {
      await api.post("/hr/payroll/adjustments", { ...form, amount: Number(form.amount), recurring: true });
      toast.success("Recurring item saved — it will post every period automatically");
      setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const stop = async (r) => {
    try { await api.post(`/hr/payroll/adjustments/${r.id}/stop-recurring`); toast.success("Recurrence stopped"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const materialise = async () => {
    try {
      const res = await api.post("/hr/payroll/adjustments/materialise", { period: thisPeriod() });
      toast.success(`${res.created} recurring item(s) posted into ${res.period}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-recurring-page">
      <Card>
        <SectionHead title="Recurring deductions & allowances"
          sub="Mark an item as recurring once (uniform, staff meal, equipment) and it posts automatically for every payroll period until you stop it"
          right={<div className="flex gap-2">
            <Btn variant="outline" className="h-9" onClick={materialise} data-testid="recurring-materialise"><Repeat size={14} /> Post into {thisPeriod()}</Btn>
            <Btn className="h-9" onClick={() => setForm({ employee_id: "", period: thisPeriod(), kind: "deduction", name: "", amount: "", reason: "", recurring_until: "" })}
              data-testid="recurring-new"><Plus size={14} /> New recurring item</Btn>
          </div>} />
        <Table
          testid="recurring-table" rows={rows} empty="No recurring items yet"
          columns={[
            { key: "employee_name", label: "EMPLOYEE" },
            { key: "name", label: "ITEM" },
            { key: "kind", label: "TYPE", render: (r) => <Pill status={r.kind === "deduction" ? "absent" : "active"} text={r.kind} /> },
            { key: "amount", label: "AMOUNT", render: (r) => <span className="tabular-nums font-semibold">{naira(r.amount)}</span> },
            { key: "period", label: "LAST POSTED" },
            { key: "recurring_until", label: "UNTIL", render: (r) => r.recurring_until || "Until stopped" },
            { key: "auto", label: "SOURCE", render: (r) => r.auto_generated ? "Auto-posted" : (r.created_by || "—") },
            { key: "_a", label: "", render: (r) => (
                <button onClick={() => stop(r)} data-testid={`recurring-stop-${r.id}`}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: HC.red }}>
                  <Ban size={13} /> Stop
                </button>) },
          ]}
        />
      </Card>

      {form && (
        <Card testid="recurring-form">
          <SectionHead title="New recurring item" sub="Posted into the period you choose and every period after it" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Employee">
              <Select data-testid="recurring-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                <option value="">Select employee</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.department_name}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select data-testid="recurring-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="deduction">Deduction</option>
                <option value="earning">Allowance / earning</option>
              </Select>
            </Field>
            <Field label="Name"><Input data-testid="recurring-name" value={form.name} placeholder="e.g. Uniform instalment" onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Amount (₦)"><Input data-testid="recurring-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Start period"><Input data-testid="recurring-period" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></Field>
            <Field label="Recur until (optional)"><Input data-testid="recurring-until" type="month" value={form.recurring_until} onChange={(e) => setForm({ ...form, recurring_until: e.target.value })} /></Field>
            <Field label="Reason" className="md:col-span-3"><Input data-testid="recurring-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Btn variant="outline" onClick={() => setForm(null)}>Cancel</Btn>
            <Btn onClick={save} disabled={busy} data-testid="recurring-save">Save recurring item</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
